import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import adminAPI from "../../Services/Admin/adminAPI";

const defaultMarketingData = {
  campaign_name: "",
  status: "inactive",
  start_date: "",
  end_date: "",
  smartphone_format: "",
  tablet_portrait_format: "",
  tablet_landscape_format: "",
  screen_14_inch_format: "",
  large_screen_format: "",
  duration: 5, // Valeur par défaut
  redirect_url: "",
};

const PostMarketingCard = ({ open, handleClose, id, onSuccess }) => {
  const [marketingData, setMarketingData] = useState(defaultMarketingData);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (id) {
      const fetchMarketingData = async () => {
        setIsLoading(true);
        try {
          const data = await adminAPI.getMarketingCampaign(id);
          setMarketingData({
            campaign_name: data.campaign_name,
            status: data.status,
            start_date: data.start_date,
            end_date: data.end_date,
            smartphone_format: data.smartphone_format,
            tablet_portrait_format: data.tablet_portrait_format,
            tablet_landscape_format: data.tablet_landscape_format,
            screen_14_inch_format: data.screen_14_inch_format,
            large_screen_format: data.large_screen_format,
            duration: data.duration ?? 5, // Utilise 5 si `duration` est null
            redirect_url: data.redirect_url || "",
          });
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des données de la campagne marketing:",
            error
          );
        } finally {
          setIsLoading(false);
        }
      };
      fetchMarketingData();
    }
  }, [id]);

  useEffect(() => {
    if (!open) {
      setMarketingData(defaultMarketingData);
    }
  }, [open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setMarketingData({
      ...marketingData,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (id) {
        await adminAPI.updateMarketingCampaign(id, marketingData);
      } else {
        await adminAPI.createMarketingCampaign(marketingData);
      }
      if (onSuccess) onSuccess(); // Rafraîchit la liste après modification
      handleClose();
    } catch (error) {
      console.error(
        `Erreur lors de la ${
          id ? "mise à jour" : "création"
        } de la campagne marketing:`,
        error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await adminAPI.deleteMarketingCampaign(id);
      if (onSuccess) onSuccess(); // Rafraîchit la liste après suppression
      handleClose();
    } catch (error) {
      console.error(
        "Erreur lors de la suppression de la campagne marketing:",
        error
      );
    } finally {
      setIsLoading(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        {id ? "Modifier la campagne marketing" : "Créer une campagne marketing"}
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Titre de la Campagne"
          name="campaign_name"
          value={marketingData.campaign_name}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Date de début"
          type="date"
          name="start_date"
          value={marketingData.start_date}
          onChange={handleChange}
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Date de fin"
          type="date"
          name="end_date"
          value={marketingData.end_date}
          onChange={handleChange}
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Status</InputLabel>
          <Select
            name="status"
            value={marketingData.status}
            onChange={handleChange}
          >
            <MenuItem value="active">Actif</MenuItem>
            <MenuItem value="inactive">Inactif</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Durée d'affichage (secondes)"
          name="duration"
          type="number"
          value={marketingData.duration}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <TextField
          label="URL de redirection"
          name="redirect_url"
          value={marketingData.redirect_url}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <h4>Formats de campagne</h4>
        <TextField
          label="Format Smartphone"
          name="smartphone_format"
          value={marketingData.smartphone_format}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Format Tablette Portrait"
          name="tablet_portrait_format"
          value={marketingData.tablet_portrait_format}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Format Tablette Paysage"
          name="tablet_landscape_format"
          value={marketingData.tablet_landscape_format}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Format Écran 14 pouces"
          name="screen_14_inch_format"
          value={marketingData.screen_14_inch_format}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Format Grand Écran"
          name="large_screen_format"
          value={marketingData.large_screen_format}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
      </DialogContent>
      <DialogActions>
        {id && (
          <>
            <IconButton onClick={() => setConfirmDelete(true)} color="error">
              <DeleteIcon />
            </IconButton>
            <Dialog
              open={confirmDelete}
              onClose={() => setConfirmDelete(false)}
            >
              <DialogTitle>Confirmation de suppression</DialogTitle>
              <DialogContent>
                Êtes-vous sûr de vouloir supprimer cette campagne marketing ?
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setConfirmDelete(false)}
                  color="secondary"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleDelete}
                  color="error"
                  disabled={isLoading}
                >
                  {isLoading ? "Suppression..." : "Supprimer"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        <Button onClick={handleClose} color="secondary">
          Annuler
        </Button>
        <Button onClick={handleSubmit} color="primary" disabled={isLoading}>
          {isLoading
            ? "Chargement..."
            : id
            ? "Mettre à jour"
            : "Lancer la Campagne"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostMarketingCard;
